/**
 * Endereçamento do realtime (F7.1): namespace do socket e nome dos rooms.
 *
 * Vive em `shared/types` pelo mesmo motivo do formato de `limitByStatus`: as duas pontas precisam
 * concordar sobre a string. O **servidor** monta o room para o `join` e para o `emit`; o **cliente**
 * monta o mesmo nome para pedir `connect(channel)`. Duas implementações do formato seriam duas
 * verdades sobre quem escuta o quê — e o sintoma seria silêncio, não erro.
 *
 * 🔴 **D59 — o cliente nunca instrui o servidor.** O nome que o cliente monta é filtro **local**: o
 * `join` do servidor é derivado do token, no `handleConnection`, e nenhum handler aceita nome de canal
 * vindo do cliente. Cliente que monte o nome errado simplesmente não recebe nada — falha fechada e
 * inofensiva. Se o nome viesse do cliente, `company:{outroId}:orders` seria vazamento entre tenants a
 * um `emit` de distância.
 *
 * 🔴 **D64 — todo room começa com `company:{companyId}:`.** É defesa em profundidade e ferramenta de
 * operação: prefixar por empresa torna o vazamento impossível mesmo com erro de programação (montar
 * room a partir de um `accountId` que veio de payload), e dá um prefixo único para listar e depurar
 * todas as conexões abertas de uma empresa. Por isso ninguém concatena room à mão: usa estas funções.
 */

/** Namespace dedicado do socket. Separa o realtime de qualquer outro uso futuro do mesmo servidor. */
export const REALTIME_NAMESPACE = '/realtime';

/**
 * Caminho HTTP do endpoint do engine.io, nas DUAS pontas (F71-02).
 *
 * O default do socket.io é `/socket.io`, na **raiz** do host — fora do `setGlobalPrefix('api')` da API,
 * que não alcança o servidor de socket. Se o deploy expõe a API por caminho (`https://host/api/*`),
 * `/socket.io` não roteia para lugar nenhum e o realtime nunca conecta. Sob `/api`, o socket herda o
 * roteamento que já existe para a API, sem regra nova de infra. Servidor (`@WebSocketGateway`) e cliente
 * (`io(..., { path })`) precisam concordar sobre a string — por isso ela vive aqui.
 */
export const REALTIME_SOCKET_PATH = '/api/socket.io';

/** Raiz obrigatória de todo room (D64). Também é o prefixo de busca ao depurar uma empresa. */
export function companyChannelPrefix(companyId: string): string {
  return `company:${companyId}:`;
}

/**
 * Room do painel administrativo: tudo que acontece com pedido da empresa (D63).
 *
 * `companyId` vem de `currentCompanyId` do access token, nunca de query nem de body.
 */
export function companyOrdersChannel(companyId: string): string {
  return `${companyChannelPrefix(companyId)}orders`;
}

/**
 * Room de uma conta: os hóspedes conectados àquela `PdvAccounts` (D63, consumido na F8).
 *
 * Dois hóspedes da mesma mesa caem aqui juntos porque **compartilham a conta** — é isso que faz o
 * pedido de um aparecer no extrato do outro. `companyId` e `accountId` vêm ambos dos claims do token
 * de sessão de mesa.
 */
export function accountChannel(companyId: string, accountId: string): string {
  return `${companyChannelPrefix(companyId)}account:${accountId}`;
}

/**
 * Conferir que um room pertence à empresa antes de publicar ou de derrubar.
 *
 * A montagem já garante o prefixo, então isto é a rede de segurança para quem receba o nome pronto —
 * `disconnectRoom` no fechamento de conta (D65) é o primeiro caso: o `channel` chega por parâmetro,
 * de outra lib.
 */
export function isChannelOfCompany(channel: string, companyId: string): boolean {
  return channel.startsWith(companyChannelPrefix(companyId));
}
